import { action, computed, makeObservable, override } from 'mobx';
import { SubscriberDomainParams } from 'global/hooks/useSubscriberDomainParams';
import { redirectToRoute } from 'routes/redirectToRoute';
import { SubscriberRouteV2Id } from 'routes/routesConfigV2';
import EmployeeFlowDelegate from 'store/flows/subscriber/employeeFlowDelegate';
import IHealthQuestionnaireEntryPage from 'store/modules/health-questionnaire/iHealthQuestionnaireEntryPage';
import { useContext, createContext } from 'react';

export default class HealthQuestionnaireEntryPageStore extends IHealthQuestionnaireEntryPage {
  public readonly flowDelegate: EmployeeFlowDelegate;

  public get isInitialLoading(): boolean {
    return this.flowDelegate.isFlowDataLoading || this.isPageContentLoading;
  }

  protected redirectWhenQuestionnaireAlreadyStarted(): void {
    const { groupId, subscriberId, entityId } = this.domainParams;
    redirectToRoute(SubscriberRouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE, { groupId, subscriberId, entityId });
  }

  protected redirectAfterAnswer(): void {
    this.flowDelegate.onCompleteRedirect();
  }

  public override setIsInitialDataLoading(status: boolean): void {
    super.setIsInitialDataLoading(status);
    this.flowDelegate.setIsInitialDataLoading(status);
  }

  protected override setIsActionLoading(status: boolean): void {
    super.setIsActionLoading(status);
    this.flowDelegate.setIsActionLoading(status);
  }

  constructor(domainParams: SubscriberDomainParams) {
    super(domainParams);
    this.flowDelegate = new EmployeeFlowDelegate({
      domainParams,
      nextPageId: SubscriberRouteV2Id.SUBSCRIBER_HEALTH_QUESTIONNAIRE,
    });

    makeObservable<
      HealthQuestionnaireEntryPageStore,
      | 'redirectWhenQuestionnaireAlreadyStarted'
      | 'redirectAfterAnswer'
      | 'setIsActionLoading'
      | 'setIsInitialDataLoading'
    >(this, {
      isInitialLoading: computed,
      redirectWhenQuestionnaireAlreadyStarted: action.bound,
      redirectAfterAnswer: action.bound,
      setIsInitialDataLoading: override,
      setIsActionLoading: override,
    });
  }
}

const HealthQuestionnaireEntryPageStoreContext = createContext(
  new HealthQuestionnaireEntryPageStore({ groupId: '', subscriberId: '', entityId: '' }),
);

export const useHealthQuestionnaireEntryPageStore = (): HealthQuestionnaireEntryPageStore => {
  return useContext(HealthQuestionnaireEntryPageStoreContext);
};

export { HealthQuestionnaireEntryPageStoreContext };
